import * as THREE from 'three';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { IYollukEleman, YollukTipi, YOLLUK_RENK } from './IDokumKontrol';

/**
 * Yolluk düzenleyici opsiyonları (DONMUŞ API).
 */
export interface IYollukDuzenleyiciOpts {
  kok: THREE.Object3D;                 // yolluk objelerinin ekleneceği kök (sahne grubu)
  kamera: THREE.Camera;
  domElement: HTMLElement;             // canvas
  orbitKontrol?: any;                  // .enabled alanı olan kontrol; gizmo sürüklerken kapat
  yuzeyMeshler: () => THREE.Mesh[];    // taşıma sırasında yüzeye snap için (opsiyonel kullan)
  onDegisti: (id: string, kismi: { pozisyon?: THREE.Vector3; yon?: THREE.Vector3 }) => void;
  onSecimDegisti: (id: string | null) => void;
}

type YollukMod = 'sec' | 'tasi' | 'dondur';

const Y_EKSEN = new THREE.Vector3(0, 1, 0);

/**
 * Sahnede yolluk elemanlarını gösteren ve TransformControls ile düzenlenmesini
 * sağlayan saf 3D yönetici sınıfı. Angular yok; düz TypeScript.
 *
 * Akış:
 *   senkronize(liste) → her eleman için bir Group kur/güncelle/sil.
 *   sec(id)           → highlight + (mod!=='sec' ise) gizmo attach.
 *   modAyarla(mod)    → gizmo translate/rotate/detach.
 *   objectChange      → group.position/quaternion'dan pozisyon+yon hesapla, onDegisti.
 */
export class FikoYollukDuzenleyici {
  private opts: IYollukDuzenleyiciOpts;
  private kok: THREE.Object3D;

  /** id → Group eşlemesi (yeniden kullanım için) */
  private gruplar = new Map<string, THREE.Group>();
  /** Hızlı erişim için son senkronize edilen eleman verisi */
  private elemanlar = new Map<string, IYollukEleman>();

  private transform: TransformControls;
  /** Sahneye eklenen gizmo helper objesi (getHelper varsa o, yoksa transform'un kendisi) */
  private gizmoHelper: THREE.Object3D | null = null;

  private _seciliId: string | null = null;
  private _mod: YollukMod = 'sec';
  private _gizmoMesgul = false;

  private raycaster = new THREE.Raycaster();
  private ndc = new THREE.Vector2();

  // Geçici çalışma buffer'ları (zero-allocation hot path)
  private _tmpVec = new THREE.Vector3();

  // Event referansları (cleanup için)
  private onDraggingChanged = (event: any) => this.draggingChanged(event);
  private onObjectChange = () => this.objectChange();

  constructor(opts: IYollukDuzenleyiciOpts) {
    this.opts = opts;
    this.kok = opts.kok;
    this.transformKur();
  }

  // ─────────────────────────────────────────────────────────────────────
  // TransformControls kurulumu
  // ─────────────────────────────────────────────────────────────────────

  private transformKur(): void {
    this.transform = new TransformControls(this.opts.kamera, this.opts.domElement);
    this.transform.setSpace('world');
    this.transform.setMode('translate');

    this.transform.addEventListener('dragging-changed', this.onDraggingChanged);
    this.transform.addEventListener('objectChange', this.onObjectChange);

    // Three sürümüne göre TransformControls bir Object3D olabilir veya
    // sahneye eklenmesi gereken bir helper döndürebilir. İkisini de güvenli ele al.
    const sahne = this.sahneyiBul();
    let helper: THREE.Object3D | null = null;
    try {
      const t: any = this.transform;
      if (typeof t.getHelper === 'function') {
        helper = t.getHelper();
      } else if (t.isObject3D) {
        helper = t;
      }
    } catch {
      const t: any = this.transform;
      helper = t?.isObject3D ? t : null;
    }

    if (helper) {
      this.gizmoHelper = helper;
      (sahne || this.kok).add(helper);
    }
  }

  /** kok'un en üst sahnesini bul (gizmo'yu oraya eklemek için); yoksa kok'un kendisi. */
  private sahneyiBul(): THREE.Object3D {
    let o: THREE.Object3D = this.kok;
    while (o.parent) o = o.parent;
    return o;
  }

  // ─────────────────────────────────────────────────────────────────────
  // senkronize — listeden 3D objeleri kur/güncelle/sil
  // ─────────────────────────────────────────────────────────────────────

  senkronize(yolluklar: IYollukEleman[]): void {
    const gelenIdler = new Set<string>();

    for (const eleman of yolluklar) {
      gelenIdler.add(eleman.id);
      this.elemanlar.set(eleman.id, eleman);

      let grup = this.gruplar.get(eleman.id);
      if (!grup) {
        grup = this.grupKur(eleman);
        this.gruplar.set(eleman.id, grup);
        this.kok.add(grup);
      } else {
        this.grupGuncelle(grup, eleman);
      }
    }

    // Listede olmayan id'leri kaldır + dispose
    for (const [id, grup] of this.gruplar) {
      if (!gelenIdler.has(id)) {
        this.grupDispose(grup);
        this.kok.remove(grup);
        this.gruplar.delete(id);
        this.elemanlar.delete(id);
        if (this._seciliId === id) {
          this.transform.detach();
          this._seciliId = null;
        }
      }
    }
  }

  /** Yeni bir Group + gövde mesh + yön oku kur. */
  private grupKur(eleman: IYollukEleman): THREE.Group {
    const grup = new THREE.Group();
    grup.userData['dkYollukId'] = eleman.id;

    const govde = this.govdeMeshKur(eleman);
    govde.userData['dkRol'] = 'govde';
    govde.userData['dkYollukId'] = eleman.id;
    grup.add(govde);

    const ok = this.okKur(eleman);
    ok.userData['dkRol'] = 'ok';
    grup.add(ok);

    this.grupTransformUygula(grup, eleman);
    return grup;
  }

  /** Var olan Group'u (gövde geometri/renk + ok + transform) güncelle — yeniden kullan. */
  private grupGuncelle(grup: THREE.Group, eleman: IYollukEleman): void {
    grup.userData['dkYollukId'] = eleman.id;

    let govde: THREE.Mesh | null = null;
    let ok: THREE.Object3D | null = null;
    for (const c of grup.children) {
      if (c.userData['dkRol'] === 'govde') govde = c as THREE.Mesh;
      else if (c.userData['dkRol'] === 'ok') ok = c;
    }

    // Gövde: tip/boyut değiştiyse geometriyi yenile, rengi/saydamlığı tazele.
    if (govde) {
      const yeniGeo = this.govdeGeometri(eleman);
      const eskiBoyut = govde.userData['dkBoyut'] as string | undefined;
      const yeniBoyut = this.boyutImza(eleman);
      if (eskiBoyut !== yeniBoyut) {
        govde.geometry.dispose();
        govde.geometry = yeniGeo;
        govde.userData['dkBoyut'] = yeniBoyut;
      } else {
        yeniGeo.dispose();
      }
      this.govdeMalzemeGuncelle(govde, eleman);
    } else {
      const yeni = this.govdeMeshKur(eleman);
      yeni.userData['dkRol'] = 'govde';
      yeni.userData['dkYollukId'] = eleman.id;
      grup.add(yeni);
    }

    // Yön oku: yeniden kur (ArrowHelper iç geometrisi paylaşımlı olduğundan basit tut).
    if (ok) {
      this.okDispose(ok);
      grup.remove(ok);
    }
    const yeniOk = this.okKur(eleman);
    yeniOk.userData['dkRol'] = 'ok';
    grup.add(yeniOk);

    this.grupTransformUygula(grup, eleman);
  }

  /** group.position = pozisyon; quaternion = (+Y → yon). */
  private grupTransformUygula(grup: THREE.Group, eleman: IYollukEleman): void {
    grup.position.copy(eleman.pozisyon);
    const yon = this._tmpVec.copy(eleman.yon).normalize();
    if (yon.lengthSq() < 1e-9) yon.copy(Y_EKSEN);
    grup.quaternion.setFromUnitVectors(Y_EKSEN, yon);
  }

  // ─────────────────────────────────────────────────────────────────────
  // Gövde mesh
  // ─────────────────────────────────────────────────────────────────────

  private boyutImza(eleman: IYollukEleman): string {
    return `${eleman.tip}|${eleman.capMm}|${eleman.yukseklikMm}`;
  }

  /** Tip + cap/yukseklik'ten +Y eksenli gövde geometrisi üret. */
  private govdeGeometri(eleman: IYollukEleman): THREE.BufferGeometry {
    const r = Math.max(eleman.capMm, 0.01) / 2;
    const h = Math.max(eleman.yukseklikMm, 0.01);
    switch (eleman.tip) {
      case 'besleyici':
        return new THREE.CylinderGeometry(r, r, h, 24);
      case 'hava_deligi':
      case 'dolum_girisi':
        // Koni: taban yarıçapı r, yükseklik h, +Y'ye doğru sivrilir.
        return new THREE.ConeGeometry(r, h, 24);
      case 'filtre':
        return new THREE.BoxGeometry(r * 2, h, r * 2);
      case 'sogutucu':
      case 'maca':
        // Soğutucu/maça blok
        return new THREE.BoxGeometry(r * 2, h, r * 2);
      case 'tasma':
        return new THREE.CylinderGeometry(r, r * 0.85, h, 20);
      case 'sensor':
        // Sensör: küçük küre işaret
        return new THREE.SphereGeometry(Math.max(r * 0.6, 1.5), 16, 12);
      default:
        return new THREE.CylinderGeometry(r, r, h, 24);
    }
  }

  private govdeMeshKur(eleman: IYollukEleman): THREE.Mesh {
    const geo = this.govdeGeometri(eleman);
    const mat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(YOLLUK_RENK[eleman.tip] || '#888888'),
      metalness: 0.2,
      roughness: 0.6,
      transparent: true,
      opacity: eleman.kabulEdildi === false ? 0.45 : 1,
      depthWrite: eleman.kabulEdildi === false ? false : true,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.userData['dkBoyut'] = this.boyutImza(eleman);
    return mesh;
  }

  private govdeMalzemeGuncelle(mesh: THREE.Mesh, eleman: IYollukEleman): void {
    const mat = mesh.material as THREE.MeshStandardMaterial;
    if (mat && mat.color) {
      mat.color.set(YOLLUK_RENK[eleman.tip] || '#888888');
      const saydam = eleman.kabulEdildi === false;
      mat.opacity = saydam ? 0.45 : 1;
      mat.transparent = true;
      mat.depthWrite = !saydam;
      mat.needsUpdate = true;
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Yön oku (ArrowHelper) — yön LOKAL +Y (group zaten döndürülmüş)
  // ─────────────────────────────────────────────────────────────────────

  private okKur(eleman: IYollukEleman): THREE.Object3D {
    const h = Math.max(eleman.yukseklikMm, 0.01);
    const dolum = eleman.tip === 'dolum_girisi';
    // Dolum girişi okunu belirgin yap: daha uzun + daha kalın başlık.
    const uzunluk = dolum ? h * 1.6 : h * 1.1;
    const renk = new THREE.Color(YOLLUK_RENK[eleman.tip] || '#888888');
    const ok = new THREE.ArrowHelper(
      Y_EKSEN.clone(),                 // LOKAL +Y
      new THREE.Vector3(0, h / 2, 0),  // gövdenin üstünden başla
      uzunluk,
      renk.getHex(),
      dolum ? uzunluk * 0.42 : uzunluk * 0.28,  // başlık uzunluğu
      dolum ? Math.max(eleman.capMm, 0.01) * 0.55 : Math.max(eleman.capMm, 0.01) * 0.35, // başlık genişliği
    );
    return ok;
  }

  private okDispose(ok: THREE.Object3D): void {
    const arrow = ok as THREE.ArrowHelper;
    try {
      arrow.line?.geometry?.dispose();
      (arrow.line?.material as THREE.Material)?.dispose?.();
      arrow.cone?.geometry?.dispose();
      (arrow.cone?.material as THREE.Material)?.dispose?.();
    } catch { /* yoksay */ }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Seçim + highlight
  // ─────────────────────────────────────────────────────────────────────

  sec(id: string | null): void {
    // Önceki highlight'ı kaldır
    if (this._seciliId && this._seciliId !== id) {
      const eski = this.gruplar.get(this._seciliId);
      if (eski) this.highlightAyarla(eski, false);
    }

    this._seciliId = id;

    if (!id) {
      this.transform.detach();
      return;
    }

    const grup = this.gruplar.get(id);
    if (!grup) {
      this.transform.detach();
      this._seciliId = null;
      return;
    }

    this.highlightAyarla(grup, true);

    // mod 'sec' ise attach etme, sadece highlight.
    if (this._mod === 'sec') {
      this.transform.detach();
    } else {
      this.transform.attach(grup);
    }
    // onSecimDegisti ÇAĞIRMA — component zaten çağıran taraf.
  }

  private highlightAyarla(grup: THREE.Group, aktif: boolean): void {
    for (const c of grup.children) {
      if (c.userData['dkRol'] !== 'govde') continue;
      const mat = (c as THREE.Mesh).material as THREE.MeshStandardMaterial;
      if (!mat || !mat.emissive) continue;
      if (aktif) {
        mat.emissive.set('#ffffff');
        mat.emissiveIntensity = 0.45;
      } else {
        mat.emissive.set('#000000');
        mat.emissiveIntensity = 0;
      }
      mat.needsUpdate = true;
    }
  }

  seciliId(): string | null {
    return this._seciliId;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Mod (gizmo)
  // ─────────────────────────────────────────────────────────────────────

  modAyarla(mod: YollukMod): void {
    this._mod = mod;
    const grup = this._seciliId ? this.gruplar.get(this._seciliId) : null;

    if (mod === 'tasi') {
      this.transform.setMode('translate');
      if (grup) this.transform.attach(grup);
    } else if (mod === 'dondur') {
      this.transform.setMode('rotate');
      if (grup) this.transform.attach(grup);
    } else {
      // 'sec' → detach, highlight kalsın.
      this.transform.detach();
    }
  }

  mod(): YollukMod {
    return this._mod;
  }

  // ─────────────────────────────────────────────────────────────────────
  // TransformControls event'leri
  // ─────────────────────────────────────────────────────────────────────

  private draggingChanged(event: any): void {
    const suruluyor = !!event?.value;
    this._gizmoMesgul = suruluyor;
    if (this.opts.orbitKontrol) {
      this.opts.orbitKontrol.enabled = !suruluyor;
    }
  }

  private objectChange(): void {
    if (!this._seciliId) return;
    const grup = this.gruplar.get(this._seciliId);
    if (!grup) return;

    const pozisyon = grup.position.clone();
    const yon = new THREE.Vector3(0, 1, 0).applyQuaternion(grup.quaternion).normalize();
    this.opts.onDegisti(this._seciliId, { pozisyon, yon });
    // Gövde tekrar senkronize EDİLMEZ — gizmo objeyi zaten hareket ettiriyor.
  }

  gizmoMesgul(): boolean {
    return this._gizmoMesgul;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Pointer ile eleman seçimi (raycast)
  // ─────────────────────────────────────────────────────────────────────

  elemanSecPointer(e: PointerEvent): string | null {
    const rect = this.opts.domElement.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    this.ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.ndc, this.opts.kamera);

    // Sadece yolluk Group'larının child'larını topla (gizmo hariç).
    const hedefler: THREE.Object3D[] = [];
    for (const grup of this.gruplar.values()) {
      for (const c of grup.children) {
        if (c.userData['dkRol'] === 'govde') hedefler.push(c);
      }
    }
    if (hedefler.length === 0) return null;

    const kesisimler = this.raycaster.intersectObjects(hedefler, false);
    if (!kesisimler.length) return null;

    // İlk hit'in parent'ına tırmanarak dkYollukId'yi bul.
    let o: THREE.Object3D | null = kesisimler[0].object;
    while (o) {
      const id = o.userData?.['dkYollukId'];
      if (typeof id === 'string') return id;
      o = o.parent;
    }
    return null;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Temizlik
  // ─────────────────────────────────────────────────────────────────────

  private grupDispose(grup: THREE.Group): void {
    for (const c of grup.children) {
      if (c.userData['dkRol'] === 'ok') {
        this.okDispose(c);
      } else {
        const mesh = c as THREE.Mesh;
        mesh.geometry?.dispose?.();
        const mat = mesh.material as THREE.Material | THREE.Material[];
        if (Array.isArray(mat)) mat.forEach((m) => m.dispose?.());
        else mat?.dispose?.();
      }
    }
    grup.clear();
  }

  dispose(): void {
    try {
      this.transform.detach();
    } catch { /* yoksay */ }

    this.transform.removeEventListener('dragging-changed', this.onDraggingChanged);
    this.transform.removeEventListener('objectChange', this.onObjectChange);

    // Gizmo helper'ı kök/sahneden çıkar.
    if (this.gizmoHelper && this.gizmoHelper.parent) {
      this.gizmoHelper.parent.remove(this.gizmoHelper);
    }
    this.gizmoHelper = null;

    try {
      this.transform.dispose();
    } catch { /* yoksay */ }

    // Tüm Group'ları dispose et.
    for (const [, grup] of this.gruplar) {
      this.grupDispose(grup);
      if (grup.parent) grup.parent.remove(grup);
    }
    this.gruplar.clear();
    this.elemanlar.clear();
    this._seciliId = null;
  }
}
